import React, {
  useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { parse, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Tooltip from '../../../../shared/_TBComponents/Tooltip';
import NavigatorManageTable, {
  OverflowCell,
  OverflowNameList,
  GroupsCell,
  nameSort,
  useActionMenu,
  ActionMenuWrapper,
  ActionMenuButton,
  ActionMenuContainer,
  ActionMenuDivider,
  ActionMenuItemButton,
} from '../../../navigator/components/presentational/tables/NavigatorManageTable';

const BULK_ACTIONS = [
  {
    key: 'deleteCodes', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const formatCode = (rawCode) => {
  if (!rawCode) return '';
  const s = rawCode.replace(/-/g, '');
  const m = Math.ceil(s.length / 2);
  return `${s.slice(0, m)}-${s.slice(m)}`;
};

const expirationDateSort = (rowA, rowB, columnId) => {
  const toTime = (value) => {
    if (!value) return Infinity;
    const parsed = parse(value, 'MMMM d, yyyy', new Date());
    return isValid(parsed) ? parsed.getTime() : Infinity;
  };
  return toTime(rowA.values[columnId]) - toTime(rowB.values[columnId]);
};

const CodeCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  margin: -4px -6px;
  border-radius: 4px;
  color: #1A1A1A;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(119, 167, 255, 0.15);
    color: #77A7FF;
  }
`;

const CopiedText = styled.span`
  color: #1A1A1A;
  font-weight: 700;
`;

const CodeCell = ({ row }) => {
  const [copied, setCopied] = useState(false);
  const { formattedCode, formattedPassword } = row.original;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`Easy Join Code: ${formattedCode} Password: ${formattedPassword}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // clipboard write failed silently
    }
  };

  if (copied) {
    return (
      <CodeCellWrapper onClick={handleCopy}>
        <CopiedText>COPIED!</CopiedText>
      </CodeCellWrapper>
    );
  }

  return (
    <CodeCellWrapper onClick={handleCopy} title='Copy code and password'>
      <span>
        {formattedCode || '-'}
        {' - '}
        {formattedPassword || '-'}
      </span>
      <Icon icon='duplicate' size={14} color='currentColor' />
    </CodeCellWrapper>
  );
};

CodeCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      formattedCode: PropTypes.string,
      formattedPassword: PropTypes.string,
    }),
  }).isRequired,
};

const NameCell = ({ row, column }) => (
  <OverflowNameList
    items={[{
      id: row.original.id,
      name: row.original.description || 'Untitled',
      tag: row.original.isNew ? 'NEW!' : undefined,
      tagColor: row.original.isNew ? '#1A1A1A' : undefined,
    }]}
    idPrefix='easy-join-name'
    rowId={row.original.id}
    align={column?.cellStyle?.textAlign}
  />
);

NameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      description: PropTypes.string,
      isNew: PropTypes.bool,
    }),
  }).isRequired,
  column: PropTypes.shape({
    cellStyle: PropTypes.shape({ textAlign: PropTypes.string }),
  }),
};

NameCell.defaultProps = {
  column: {},
};

const ExpiryWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => (props.isExpired ? '#FF0505' : '#1A1A1A')};
`;

const ExpiryCell = ({ row }) => {
  const {
    id, expirationDate, isExpired, expiredDisplayTime,
  } = row.original;

  if (!expirationDate) return <OverflowCell value='' />;

  return (
    <ExpiryWrapper isExpired={isExpired}>
      {expirationDate}
      <Tooltip
        id={`easy-join-expiry-${id}`}
        icon='info2'
        iconSize={13}
        hoverText={isExpired
          ? `Expired at: ${expiredDisplayTime || expirationDate}`
          : `Expires at: ${expiredDisplayTime || expirationDate}`}
        displayColor='#9E9E9E'
        place='left'
        cursor='default'
      />
    </ExpiryWrapper>
  );
};

ExpiryCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      expirationDate: PropTypes.string,
      isExpired: PropTypes.bool,
      expiredDisplayTime: PropTypes.string,
    }),
  }).isRequired,
};

const ActionDropdownCell = ({ row }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();

  return (
    <ActionMenuWrapper onClick={(e) => e.stopPropagation()}>
      <ActionMenuButton ref={buttonRef} type='button' onClick={openMenu}>
        <Icon icon='dots' size={16} color='currentColor' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenuContainer ref={menuRef} style={menuStyle}>
          <ActionMenuItemButton type='button' onClick={() => { row.original.onEdit(row.original); close(); }}>
            <Icon icon='edit-workout' size={16} color='#1A1A1A' />
            Edit
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' onClick={() => { row.original.onDelete(row.original); close(); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            Delete
          </ActionMenuItemButton>
        </ActionMenuContainer>,
        document.body,
      )}
    </ActionMenuWrapper>
  );
};

ActionDropdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const easyJoinSearch = (row, q) => {
  const haystack = [
    row.description,
    row.formattedCode,
    row.calendarName,
    ...(row.groups || []).map((g) => g.name),
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(q);
};

const EasyJoinTable = React.forwardRef(({
  codes, isLoadingCodes, onEdit, onDeleteCode, onBulkAction, onVisibleCodesChange,
  bannerContent, newCodeId,
}, ref) => {
  const innerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    scrollToCode: (id) => innerRef.current?.scrollToRow(id),
  }), []);

  const tableData = useMemo(
    () => codes.map((code) => {
      const isExpired = (() => {
        if (!code.expirationDate) return false;
        const parsed = parse(code.expirationDate, 'MMMM d, yyyy', new Date());
        return isValid(parsed) && parsed < new Date();
      })();

      return {
        ...code,
        groupAssignments: code.groups,
        calendarName: code.calendar?.name || '',
        isExpired,
        isNew: code.id === newCodeId,
        formattedCode: formatCode(code.joinCode),
        formattedPassword: code.joinPassword ? code.joinPassword.replace(/-/g, '') : '',
        onEdit,
        onDelete: onDeleteCode,
      };
    }),
    [codes, newCodeId, onEdit, onDeleteCode],
  );

  const columns = useMemo(() => [
    {
      Header: 'Name',
      id: 'description',
      accessor: (row) => row.description || 'Untitled',
      sortType: nameSort,
      headerStyle: { minWidth: '180px', flex: '1', textAlign: 'left' },
      cellStyle: {
        minWidth: '180px', flex: '1', overflow: 'hidden', textAlign: 'left',
      },
      Cell: NameCell,
    },
    {
      Header: 'Code & Password',
      accessor: 'formattedCode',
      headerStyle: { width: '260px', textAlign: 'left' },
      cellStyle: { width: '260px', textAlign: 'left' },
      Cell: CodeCell,
    },
    {
      Header: 'Groups',
      id: 'groups',
      accessor: (row) => (row.groups || []).map((g) => g.name).join(', '),
      sortType: nameSort,
      headerStyle: { minWidth: '160px', flex: '1', textAlign: 'left' },
      cellStyle: {
        minWidth: '160px', flex: '1', overflow: 'hidden', textAlign: 'left',
      },
      Cell: GroupsCell,
    },
    {
      Header: 'Calendar',
      accessor: 'calendarName',
      sortType: nameSort,
      headerStyle: { width: '160px', textAlign: 'left' },
      cellStyle: { width: '160px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: 'Valid Until',
      accessor: 'expirationDate',
      sortType: expirationDateSort,
      headerStyle: { width: '200px', textAlign: 'left' },
      cellStyle: { width: '200px', textAlign: 'left' },
      Cell: ExpiryCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '90px', textAlign: 'center' },
      cellStyle: { width: '90px', textAlign: 'center' },
      Cell: ActionDropdownCell,
    },
  ], []);

  return (
    <NavigatorManageTable
      ref={innerRef}
      data={tableData}
      columns={columns}
      isLoading={isLoadingCodes}
      searchFn={easyJoinSearch}
      searchPlaceholder='Search for a code'
      emptyMessage='No Easy Join codes yet. Create one to get started.'
      searchEmptyMessage='No codes match your search.'
      entityLabel='code'
      defaultSortBy={[{ id: 'description', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={onBulkAction}
      onFilteredDataChange={onVisibleCodesChange}
      bannerContent={bannerContent}
      bannerPosition='top'
    />
  );
});

EasyJoinTable.displayName = 'EasyJoinTable';

EasyJoinTable.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  isLoadingCodes: PropTypes.bool,
  onEdit: PropTypes.func,
  onDeleteCode: PropTypes.func,
  onBulkAction: PropTypes.func,
  onVisibleCodesChange: PropTypes.func,
  bannerContent: PropTypes.node,
  newCodeId: PropTypes.number,
};

EasyJoinTable.defaultProps = {
  isLoadingCodes: false,
  onEdit: () => {},
  onDeleteCode: () => {},
  onBulkAction: () => {},
  onVisibleCodesChange: () => {},
  newCodeId: null,
  bannerContent: null,
};

export default EasyJoinTable;
